import React, { useState, useEffect } from 'react';
import { 
  Key, 
  Sparkles, 
  ShieldCheck, 
  Zap, 
  Cpu, 
  Brain, 
  Globe, 
  Layers, 
  Search, 
  Shield, 
  CheckCircle2, 
  AlertCircle, 
  SlidersHorizontal,
  ChevronRight,
  TrendingUp,
  Activity
} from 'lucide-react';
import { 
  loadByokVault, 
  getActiveAiConfig, 
  AI_PROVIDERS_CONFIG, 
  ByokVaultState, 
  AiProviderId 
} from '../utils/byokService';

interface MultiProviderBYOKCardProps {
  onOpenModal: (providerId?: AiProviderId) => void;
  className?: string;
  compact?: boolean;
}

export function MultiProviderBYOKCard({
  onOpenModal,
  className = '',
  compact = false
}: MultiProviderBYOKCardProps) {
  const [vault, setVault] = useState<ByokVaultState>(() => loadByokVault());

  useEffect(() => {
    const handleVaultUpdate = () => {
      setVault(loadByokVault());
    };
    window.addEventListener('azuraos_byok_updated', handleVaultUpdate);
    window.addEventListener('storage', handleVaultUpdate);
    return () => {
      window.removeEventListener('azuraos_byok_updated', handleVaultUpdate);
      window.removeEventListener('storage', handleVaultUpdate);
    };
  }, []);

  const activeId = vault.activeProvider || 'gemini';
  const activeConfig = AI_PROVIDERS_CONFIG[activeId];
  const activeState = vault.providers[activeId];
  const hasKey = Boolean(activeState?.apiKey && activeState.apiKey.trim().length >= 6);
  const isVerified = activeState?.status === 'verified';
  const connectedCount = Object.values(vault.providers).filter(p => p.apiKey && p.apiKey.trim().length >= 6).length;

  const getProviderIcon = (providerId: AiProviderId, size = 16) => {
    switch (providerId) {
      case 'gemini': return <Sparkles size={size} className="text-blue-500" />;
      case 'groq': return <Zap size={size} className="text-orange-500" />;
      case 'openai': return <Cpu size={size} className="text-emerald-500" />;
      case 'anthropic': return <Brain size={size} className="text-amber-600" />;
      case 'deepseek': return <Layers size={size} className="text-indigo-500" />;
      case 'openrouter': return <Globe size={size} className="text-violet-500" />;
      case 'mistral': return <Shield size={size} className="text-amber-500" />;
      case 'perplexity': return <Search size={size} className="text-teal-500" />;
      default: return <Key size={size} className="text-slate-400" />;
    }
  };

  if (compact) {
    return (
      <button
        onClick={() => onOpenModal(activeId)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all text-xs ${
          hasKey
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20'
            : 'bg-indigo-500/10 border-indigo-500/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/20'
        } ${className}`}
        title="Manage Multi-Provider BYOK Keys (Google AI Studio, OpenAI, Claude, Groq, DeepSeek)"
      >
        <div className="flex items-center gap-1.5">
          {getProviderIcon(activeId, 14)}
          <span className="font-bold">{activeConfig.name.split(' ')[0]}</span>
          <span className="text-[10px] opacity-75 font-mono">
            ({activeState?.selectedModel?.split('-')[0] || 'default'})
          </span>
        </div>
        {hasKey ? (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={11} /> Connected ({connectedCount}/8)
          </span>
        ) : (
          <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 underline">
            + Connect Key
          </span>
        )}
      </button>
    );
  }

  return (
    <div className={`p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white shadow-xl ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        
        {/* Left: Active Provider Info */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-white/10 border border-white/10 text-white shadow-inner">
            {getProviderIcon(activeId, 20)}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-300">
                Active BYOK Intelligence Engine
              </span>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white">
                {connectedCount} of 8 Ready
              </span>
              {vault.autoFailoverEnabled && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                  <Activity size={10} /> Failover Active
                </span>
              )}
            </div>
            <div className="text-sm font-bold text-white flex items-center gap-2 mt-0.5">
              <span>{activeConfig.name}</span>
              <span className="text-xs font-normal text-slate-300 font-mono">
                • {activeState?.selectedModel || activeConfig.defaultModel}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Action & Telemetry */}
        <div className="flex items-center gap-2">
          {vault.telemetry?.estimatedCostSavedUsd > 0 && (
            <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-emerald-400">
              <TrendingUp size={13} />
              <span>${vault.telemetry.estimatedCostSavedUsd.toFixed(2)} Saved</span>
            </div>
          )}

          <button
            onClick={() => onOpenModal(activeId)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-indigo-500 hover:bg-indigo-600 text-white shadow-md transition-all active:scale-95"
          >
            <SlidersHorizontal size={14} />
            <span>Manage BYOK Vault</span>
            <ChevronRight size={14} />
          </button>
        </div>

      </div>

      {/* Quick provider icons bar */}
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-2 overflow-x-auto text-[11px] text-slate-300">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Quick Switch:</span>
          {(Object.keys(AI_PROVIDERS_CONFIG) as AiProviderId[]).map((pid) => {
            const pState = vault.providers[pid];
            const isCurrent = activeId === pid;
            const isConf = Boolean(pState?.apiKey && pState.apiKey.trim().length >= 6);

            return (
              <button
                key={pid}
                onClick={() => onOpenModal(pid)}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all text-xs ${
                  isCurrent 
                    ? 'bg-white text-slate-900 font-bold shadow' 
                    : isConf
                    ? 'bg-white/10 text-white hover:bg-white/20'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
                title={`Configure ${AI_PROVIDERS_CONFIG[pid].name}`}
              >
                {getProviderIcon(pid, 12)}
                <span className="capitalize">{pid}</span>
                {isConf && !isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
              </button>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-1.5 text-slate-400 text-[10px]">
          <ShieldCheck size={12} className="text-emerald-400" />
          <span>Zero-Custody Client AES</span>
        </div>
      </div>
    </div>
  );
}
